import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {FormModel} from '../../form/form.model.abstract';
import {StepComponent} from '../../step/step.component';
import {EingabeForm} from './eingabe.form';
import {Step, StepService} from '../../services/step.service';
import {Router} from '@angular/router';
import {StromcheckRequestService} from '../services/request.service';
import {StromcheckAnfrage} from '../anfrage/stromcheck.anfrage';
import {ElementService} from '../../services/element.service';
import {PortalService} from '../../services/portal.service';


/**
 * Custom step for the input of electricity invoice on the step "verbrauchsdaten"
 *
 * @inheritDoc
 */
@Component({
  selector: 'app-step-eingabe-verbrauchsdaten',
  templateUrl: './eingabe.step.verbrauchsdaten.component.html'
})
export class EingabeStepVerbrauchsdatenComponent extends StepComponent implements OnInit {
  /**
   * form fields of invoice
   */
  invoice: any;

  /**
   * toggle that is true while sending/receiving data to/from backend
   */
  loadingData: boolean = false;

  /**
   * list of backend errors to be shown on the frontend
   */
  backendErrors = [];

  /**
   * List of errors for the invoice.
   */
  invoiceErrors: { error?: string, fields?: string[]} = null;

  /**
   * placeholders for the errors
   */
  placeholderErrors: {};

  /**
   * The wrapper for the stepform
   */
  @ViewChild('formWrapper') formWrapper: ElementRef;

  /**
   * Holds the URL to the entrystep
   */
  entryUrl: string;

  /**
   * anfrage object
   */
  anfrage: StromcheckAnfrage;

  /**
   * @inheritDoc
   */
  formConfig: EingabeForm;

  /**
   * Overwrite the label prefix.
   */
  textkeyPrefix = 'stromcheck';

  /**
   * @inheritDoc
   */
  constructor(protected stepService: StepService, protected router: Router, protected requestService: StromcheckRequestService,
              protected elementService: ElementService, protected portalService: PortalService) {
    super(stepService, router, elementService);
  }

  /**
   * @inheritDoc
   */
  ngOnInit() {
    // add the correct form config that we need in this steps and init the parent form
    this.formConfig = new EingabeForm();
    super.ngOnInit();

    // get the current input data and preset it
    this.anfrage = this.requestService.getAnfrage();
    this.formConfig.anfrage = this.anfrage;
    this.formConfig.setData(this.anfrage.userInput);

    this.formConfig.updateFields(this.anfrage.userInput);
    this.invoice = this.formConfig.getFieldsFromGroup(this.steps.currentStep.name);

    // update the previous step
    this.onStepUpdate();
    this.steps.nextStep = new Step('ergebnis/analyse', '/stromcheck/ergebnis/analyse');

    // if there are changes in the form, reset the warnings
    this.subscriptions.push(this.form.valueChanges.subscribe(() => {
      this.formWarning = null;
    }));
  }

  /**
   * @inheritDoc
   * @param data
   */
  saveData(data: { [key: string]: any }) {
    // merge the data with the existing data
    this.anfrage.userInput = {...this.anfrage.userInput, ...data};
    this.requestService.setAnfrage(this.anfrage);

    // save the data also to the server
    this.requestService.saveDataToServer();

    super.saveData(data);
  }

  /**
   * show form errors if the form should be submitted
   */
  showFormErrors(): void {
    this.formError = 'emptyfield';
    // check if there are formerrors set, if yes, show them
    if (this.form.errors) {
      this.formConfig.invalidateFieldsOfFormvalidators(this.form.errors);
      Object.keys(this.form.errors).map(key => this.formError = key);
    }
    // stop here, form is not valid
    this.elementService.scrollToElement(this.formWrapper.nativeElement);
  }

  /**
   * @inheritDoc
   */
  async onSubmit() {
    this.resetErrors();

    if (this.form.valid === false) {
      this.showFormErrors();
      return;
    }

    // save the input up to here
    this.saveData(this.formConfig.getData());

    // start loading animation
    this.loadingData = true;

    // validate invoice
    this.validateInvoice();
  }

  /**
   * resets the visible errors in all invoices
   */
  resetErrors(): void {
    this.formConfig.resetFormValidatorsFromFields();
    this.formError = null;
    this.invoiceErrors = null;
    this.backendErrors = [];
    this.placeholderErrors = {};
  }

  /**
   * validates one single invoice with the backend.
   */
  validateInvoice(): void {
    this.requestService.backendService.getRequestId().subscribe(requestId => {
      // reset mobile car electricity to avoid validation errors
      this.anfrage.userInput.ladestrom_elektrofahrzeug_verbrauch = null;
      this.anfrage.userInput.elektrofahrzeug_vorhanden = null;
      this.anfrage.userInput.ladestrom_unbekannt = null;

      // reset user input to electricity price to avoid validation errors
      this.anfrage.userInput.grundpreis_euro_monat = null;
      this.anfrage.userInput.arbeitspreis_cent_kwh = null;

      // calculate result
      this.requestService.backendService.result(this.anfrage, requestId).subscribe(data => {
        this.loadingData = false;
        this.invoiceErrors = null;
        this.backendErrors = [];
        this.placeholderErrors = {};
        this.anfrage.result = data;
        this.requestService.setAnfrage(this.anfrage);
        this.router.navigateByUrl(this.portalService.currentPortalId + '/stromcheck/ergebnis/analyse');
      }, error => {
        this.loadingData = false;
        if (error?.status === 400) {
          this.invoiceErrors = {error: 'input', fields: error.error?.result?.status?.errors[0]?.affected_fields || []};
          // show specific error message, based on backend reply
          this.backendErrors = error.error?.result?.status ? error.error.result.status.errors.map(err => {
            return this.textkeyPrefix + '.stromkostenabrechnung.error.' + err.code;
          }) : [];
          this.placeholderErrors = error.error?.result?.status?.errors[0]?.placeholders || {};
        }
        else {
          // generic error, mostly on the connection to the backend.
          this.formError = 'connection';
          console.error("Could not get data from backend", error);
        }
        this.elementService.scrollToElement(this.stepForm.nativeElement);
      });
    }, error => {
      this.loadingData = false;
      // an error occured while talking to the backend.
      console.error("Could not get requestid from backend", error);
    });
  }

  /**
   * Show error mark on affected fields of invalid forms
   * @param field
   */
  isAffectedFieldForError(field: FormModel): boolean {
    // check if at least on of the field ids is in the list of affected fields
    if (this.invoiceErrors) {
      const fieldIds = field.getIds();
      // check if one of the ids is in the affected field list
      return fieldIds.filter(id => this.invoiceErrors.fields.indexOf(id) > -1).length > 0;
    }
    return false;
  }

}
